/**
 * Format a date as relative time, long form.
 *
 * Used in account-status contexts (e.g. "Updated 5 mins ago", "Updated 2 hours ago").
 * Falls back to a locale date string after 7 days.
 *
 * @param {Date|string|number|null|undefined} date
 * @returns {string|null} formatted string, or null when no date provided
 */
export const formatRelativeTime = (date) => {
  if (!date) return null;
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
  return dateObj.toLocaleDateString();
};

/**
 * Format a date as relative time, short feed form.
 *
 * Used in social/feed contexts (e.g. "5h ago", "Yesterday", "3d ago").
 *
 * @param {Date|string|number} date
 * @returns {string} formatted string
 */
export const formatRelativeTimeShort = (date) => {
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
};

const DATE_ONLY_YMD = /^\d{4}-\d{2}-\d{2}$/;

export const toDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Inclusive one-day window for day sheets (#1547). */
export const dateOnlyDayRange = (dateKey) => {
  const trimmed = String(dateKey || '').trim();
  if (!DATE_ONLY_YMD.test(trimmed)) return null;
  return { startDate: trimmed, endDate: trimmed };
};

/**
 * Inclusive Activity presets as YYYY-MM-DD. Web week stays rolling-7.
 */
export const dateOnlyPresetRange = (preset, now = new Date(), options = {}) => {
  if (!preset || preset === 'any' || preset === 'all') return null;
  const today = toDateOnly(now);
  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'week': {
      if (options.weekMode === 'sunday') {
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        return { startDate: toDateOnly(weekStart), endDate: today };
      }
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      return { startDate: toDateOnly(start), endDate: today };
    }
    case 'month':
      return { startDate: toDateOnly(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: today };
    case 'year':
      return { startDate: toDateOnly(new Date(now.getFullYear(), 0, 1)), endDate: today };
    default:
      return null;
  }
};
